import { downloadExcelWorkbook } from './exportExcel';

const normalizeText = (value) => String(value ?? '').trim();
const normalizeNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, numeric) : 0;
};
const normalizeSignedNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const shiftDate = (dateKey, days) => {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setDate(date.getDate() + days);
  return formatDateKey(date);
};

const buildProductKey = (product) => normalizeText(product.sku || product.id || product.name).toLowerCase();

const totalFor = (quantity, rate) => normalizeNumber(quantity) * normalizeNumber(rate);

export const calculateRowMetrics = (row = {}) => {
  const openingQuantity = normalizeNumber(row.openingQuantity);
  const openingRate = normalizeNumber(row.openingRate);
  const purchaseQuantity = normalizeNumber(row.purchaseQuantity);
  const purchaseRate = normalizeNumber(row.purchaseRate);
  const salesQuantity = normalizeNumber(row.salesQuantity);
  const closingQuantity = normalizeNumber(row.closingQuantity);
  const closingRate = normalizeNumber(row.closingRate);
  const wastageQuantity = normalizeNumber(row.wastageQuantity);
  const wastageRate = normalizeNumber(row.wastageRate);
  const expectedClosing = openingQuantity + purchaseQuantity - salesQuantity - wastageQuantity;
  const variance = closingQuantity - expectedClosing;

  return {
    ...row,
    openingQuantity,
    openingRate,
    openingTotal: totalFor(openingQuantity, openingRate),
    purchaseQuantity,
    purchaseRate,
    purchaseTotal: totalFor(purchaseQuantity, purchaseRate),
    salesQuantity,
    closingQuantity,
    closingRate,
    closingTotal: totalFor(closingQuantity, closingRate),
    wastageQuantity,
    wastageRate,
    wastageTotal: totalFor(wastageQuantity, wastageRate),
    expectedClosing,
    variance,
    varianceStatus: variance === 0 ? 'valid' : 'warning',
  };
};

export const createProduceRow = (product = {}, defaults = {}) => calculateRowMetrics({
  id: normalizeText(product.id) || `produce-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productId: normalizeText(product.id),
  productName: normalizeText(product.name || product.product),
  sku: normalizeText(product.sku),
  openingQuantity: defaults.openingQuantity ?? 0,
  openingRate: defaults.openingRate ?? 0,
  purchaseQuantity: defaults.purchaseQuantity ?? 0,
  purchaseRate: defaults.purchaseRate ?? 0,
  salesQuantity: defaults.salesQuantity ?? 0,
  closingQuantity: defaults.closingQuantity ?? 0,
  closingRate: defaults.closingRate ?? 0,
  wastageQuantity: defaults.wastageQuantity ?? 0,
  wastageRate: defaults.wastageRate ?? 0,
});

export const normalizeProduceRecord = (record = {}) => {
  const rows = Array.isArray(record.rows) ? record.rows.map(calculateRowMetrics) : [];
  return {
    id: normalizeText(record.id) || `produce-record-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    date: normalizeText(record.date) || formatDateKey(),
    storeId: normalizeText(record.storeId),
    storeName: normalizeText(record.storeName),
    status: normalizeText(record.status) === 'completed' ? 'completed' : 'draft',
    createdAt: normalizeText(record.createdAt) || new Date().toISOString(),
    updatedAt: normalizeText(record.updatedAt) || new Date().toISOString(),
    completedAt: normalizeText(record.completedAt) || '',
    createdBy: normalizeText(record.createdBy),
    updatedBy: normalizeText(record.updatedBy),
    rows,
  };
};

export const findProduceRecord = (records = [], { storeId, date }) => (
  records.find((record) => String(record.storeId || '') === String(storeId || '') && record.date === date) || null
);

export const getPreviousProduceRecord = (records = [], { storeId, date }) => {
  const previousDate = shiftDate(date, -1);
  return findProduceRecord(records, { storeId, date: previousDate });
};

export const getLatestRateMap = (records = [], storeId) => {
  const map = new Map();
  const scopedRecords = records
    .filter((record) => !storeId || String(record.storeId || '') === String(storeId))
    .sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime() || new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime());

  scopedRecords.forEach((record) => {
    record.rows.forEach((row) => {
      const key = buildProductKey(row);
      if (!key || map.has(key)) return;
      const candidateRates = [
        row.purchaseRate,
        row.closingRate,
        row.openingRate,
        row.wastageRate,
      ].map(normalizeNumber);
      const foundRate = candidateRates.find((rate) => rate > 0);
      if (foundRate) {
        map.set(key, foundRate);
      }
    });
  });

  return map;
};

export const buildProduceRowsForDate = ({
  products = [],
  records = [],
  storeId = '',
  date = formatDateKey(),
  existingRecord = null,
}) => {
  const mergedProducts = products;
  const previousRecord = getPreviousProduceRecord(records, { storeId, date });
  const latestRateMap = getLatestRateMap(records, storeId);
  const existingRowMap = new Map((existingRecord?.rows || []).map((row) => [buildProductKey(row), row]));
  const previousRowMap = new Map((previousRecord?.rows || []).map((row) => [buildProductKey(row), row]));

  return mergedProducts.map((product) => {
    const key = buildProductKey(product);
    const existingRow = existingRowMap.get(key);
    if (existingRow) {
      return calculateRowMetrics(existingRow);
    }

    const previousRow = previousRowMap.get(key);
    const rateFallback = latestRateMap.get(key) || 0;
    return createProduceRow(product, {
      openingQuantity: previousRow?.closingQuantity ?? 0,
      openingRate: previousRow?.closingRate || previousRow?.purchaseRate || previousRow?.openingRate || rateFallback,
      purchaseRate: previousRow?.purchaseRate || previousRow?.closingRate || previousRow?.openingRate || rateFallback,
      salesQuantity: previousRow?.salesQuantity ?? 0,
      closingRate: previousRow?.closingRate || previousRow?.purchaseRate || previousRow?.openingRate || rateFallback,
      wastageRate: previousRow?.wastageRate || previousRow?.purchaseRate || previousRow?.closingRate || rateFallback,
    });
  });
};

export const updateProduceRowField = (row, field, value) => calculateRowMetrics({
  ...row,
  [field]: value,
});

export const clearProduceField = (rows = [], field) => {
  return rows.map((row) => calculateRowMetrics({
    ...row,
    [field]: 0,
  }));
};

export const calculateProduceSummary = (rows = []) => {
  const totalUnits = rows.reduce((sum, row) => sum + normalizeNumber(row.openingQuantity + row.purchaseQuantity + row.closingQuantity + row.wastageQuantity), 0);
  const varianceValues = rows.map((row) => normalizeSignedNumber(row.variance));
  const varianceTotal = varianceValues.reduce((sum, value) => sum + Math.abs(value), 0);
  const varianceNet = varianceValues.reduce((sum, value) => sum + value, 0);
  const varianceCount = varianceValues.filter((value) => value !== 0).length;

  return {
    totalProducts: rows.length,
    openingSkuCount: rows.filter((row) => normalizeNumber(row.openingQuantity) > 0).length,
    openingUnits: rows.reduce((sum, row) => sum + normalizeNumber(row.openingQuantity), 0),
    openingAmount: rows.reduce((sum, row) => sum + normalizeNumber(row.openingTotal), 0),
    purchasedSkuCount: rows.filter((row) => normalizeNumber(row.purchaseQuantity) > 0).length,
    purchasedUnits: rows.reduce((sum, row) => sum + normalizeNumber(row.purchaseQuantity), 0),
    purchasedAmount: rows.reduce((sum, row) => sum + normalizeNumber(row.purchaseTotal), 0),
    closingSkuCount: rows.filter((row) => normalizeNumber(row.closingQuantity) > 0).length,
    closingUnits: rows.reduce((sum, row) => sum + normalizeNumber(row.closingQuantity), 0),
    closingAmount: rows.reduce((sum, row) => sum + normalizeNumber(row.closingTotal), 0),
    wastageSkuCount: rows.filter((row) => normalizeNumber(row.wastageQuantity) > 0).length,
    wastageUnits: rows.reduce((sum, row) => sum + normalizeNumber(row.wastageQuantity), 0),
    wastageAmount: rows.reduce((sum, row) => sum + normalizeNumber(row.wastageTotal), 0),
    salesUnits: rows.reduce((sum, row) => sum + normalizeNumber(row.salesQuantity), 0),
    totalUnits,
    varianceTotal,
    varianceNet,
    varianceCount,
    varianceStatus: varianceCount === 0 ? 'valid' : 'warning',
  };
};

export const buildProduceReportRows = (records = []) => (
  records.flatMap((record) => record.rows.map((row) => ({
    recordId: record.id,
    date: record.date,
    storeId: record.storeId,
    storeName: record.storeName,
    status: record.status,
    completedAt: record.completedAt,
    updatedAt: record.updatedAt,
    updatedBy: record.updatedBy,
    productName: row.productName,
    sku: row.sku,
    openingQuantity: row.openingQuantity,
    openingRate: row.openingRate,
    openingTotal: row.openingTotal,
    purchaseQuantity: row.purchaseQuantity,
    purchaseRate: row.purchaseRate,
    purchaseTotal: row.purchaseTotal,
    salesQuantity: row.salesQuantity,
    closingQuantity: row.closingQuantity,
    closingRate: row.closingRate,
    closingTotal: row.closingTotal,
    wastageQuantity: row.wastageQuantity,
    wastageRate: row.wastageRate,
    wastageTotal: row.wastageTotal,
    expectedClosing: row.expectedClosing,
    variance: row.variance,
    varianceStatus: row.varianceStatus,
  })))
);

export const exportProduceReportCsv = async (rows = [], filename = 'produce-inventory-report.xlsx') => {
  if (typeof window === 'undefined') return;
  const columns = [
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Store', key: 'storeName', width: 18 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Product', key: 'productName', width: 22 },
    { header: 'SKU', key: 'sku', width: 16 },
    { header: 'Opening Qty', key: 'openingQuantity', width: 14 },
    { header: 'Opening Rate', key: 'openingRate', width: 14 },
    { header: 'Opening Total', key: 'openingTotal', width: 14 },
    { header: 'Purchase Qty', key: 'purchaseQuantity', width: 14 },
    { header: 'Purchase Rate', key: 'purchaseRate', width: 14 },
    { header: 'Purchase Total', key: 'purchaseTotal', width: 14 },
    { header: 'Sales Qty', key: 'salesQuantity', width: 12 },
    { header: 'Closing Qty', key: 'closingQuantity', width: 12 },
    { header: 'Closing Rate', key: 'closingRate', width: 12 },
    { header: 'Closing Total', key: 'closingTotal', width: 12 },
    { header: 'Wastage Qty', key: 'wastageQuantity', width: 12 },
    { header: 'Wastage Rate', key: 'wastageRate', width: 12 },
    { header: 'Wastage Total', key: 'wastageTotal', width: 12 },
    { header: 'Expected Closing', key: 'expectedClosing', width: 16 },
    { header: 'Variance', key: 'variance', width: 12 },
    { header: 'Updated By', key: 'updatedBy', width: 18 },
    { header: 'Updated At', key: 'updatedAt', width: 20 },
  ];
  await downloadExcelWorkbook({
    fileName: filename.endsWith('.xlsx') ? filename : `${filename.replace(/\.csv$/i, '')}.xlsx`,
    sheetName: 'Produce Inventory Report',
    columns,
    rows,
  });
};

export { formatDateKey };
